import React, { useState } from 'react';
import { PayWithCoinsCard, PayWithCoinsCardSpendingState, Modal, CoinBalance, Button, LoadingOverlay } from 'cf-ui';
import { useIntl } from 'react-intl';

import {
  useCoinsSettingsQuery,
  useCoinsBalanceQuery,
  useCoinsTransactionForProductIdQuery,
  useCoinsUpdateTransactionStatusMutation,
  useCoinsSpendOnProductMutation,
  CoinsSettingsQuery,
  CoinsTransactionForProductIdQuery,
  CoinTransactionStatus,
} from '../../generated/graphql';
import { ProductPayWithCoinsCardProps, ProductPayWithCoinsType } from './ProductPayWithCoinsCard.types';
import * as S from './ProductPayWithCoinsCard.styles';

export const ERROR_TRANSACTION_NOT_FOUND = 'transaction_not_found';

const decideBundeValue = (data: CoinsSettingsQuery, type: ProductPayWithCoinsType) => {
  if (type === ProductPayWithCoinsType.BUNDLE) {
    return Number(data?.coinsSettings.spending?.products.bundles);
  }

  if (type === ProductPayWithCoinsType.CLASS) {
    return Number(data?.coinsSettings.spending?.products.classes);
  }

  return Number(data?.coinsSettings.spending?.products.crafts);
};

const decideCardState = (data: CoinsTransactionForProductIdQuery) => {
  if (data.coinsTransactionForProductId.transaction?.status === ('cancelled' as CoinTransactionStatus)) {
    return PayWithCoinsCardSpendingState.cancelled;
  }

  if (
    data.coinsTransactionForProductId.transaction?.status === ('pending' as CoinTransactionStatus) ||
    data.coinsTransactionForProductId.transaction?.status === ('confirmed' as CoinTransactionStatus)
  ) {
    return PayWithCoinsCardSpendingState.confirmed;
  }

  return PayWithCoinsCardSpendingState.ready_to_spend;
};

const decideCoinsBalanceByType = (type: ProductPayWithCoinsType, bundleValue: number, userBalance: number) => {
  if (type === ProductPayWithCoinsType.BUNDLE) {
    return <CoinBalance.BundlesProgress max={bundleValue} current={userBalance} />;
  }

  if (type === ProductPayWithCoinsType.CLASS) {
    return <CoinBalance.ClassesProgress max={bundleValue} current={userBalance} />;
  }

  return <CoinBalance.CraftsProgress max={bundleValue} current={userBalance} />;
};

const decideCardTitle = (type: ProductPayWithCoinsType) => {
  if (type === ProductPayWithCoinsType.CLASS) {
    return 'card_coin_spending.classes_title';
  }

  return 'card_coin_spending.title';
};

export const ProductPayWithCoinsCard = (props: ProductPayWithCoinsCardProps) => {
  const { id, type, name, domElement } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: settingsData, isLoading: settingsDataLoading } = useCoinsSettingsQuery();
  const { data: balanceData, isLoading: balanceDataLoading } = useCoinsBalanceQuery();
  const { data: productData, isLoading: productDataLoading } = useCoinsTransactionForProductIdQuery({
    productId: `${id}`,
  });

  const updateCoinTransactionStatus = useCoinsUpdateTransactionStatusMutation();
  const spendCoinsOnProduct = useCoinsSpendOnProductMutation();
  const { formatMessage: t } = useIntl();

  //At least one request still on flight
  if (!settingsData || !balanceData || !productData) {
    //We do not hide the container here because one of the requests might still be in flight and we will have content shift
    return (
      <LoadingOverlay isLoading={settingsDataLoading || balanceDataLoading || productDataLoading}>
        <S.Wrapper />
      </LoadingOverlay>
    );
  }

  if (
    settingsData.coinsSettings?.errors ||
    (productData?.coinsTransactionForProductId?.errors &&
      !productData?.coinsTransactionForProductId?.errors[0].message.includes(ERROR_TRANSACTION_NOT_FOUND))
  ) {
    //Hide the container element in case of API errors
    //We will have content shift but error cases should be minimals
    domElement.classList.add('u-hide');

    return null;
  }

  const bundleValue = decideBundeValue(settingsData, type);
  const userBalance = balanceData.coins.balance || 0;
  const canCancel = productData.coinsTransactionForProductId.transaction?.status === 'pending';
  const status = productData?.coinsTransactionForProductId?.transaction?.status;

  const state =
    userBalance < bundleValue && !Boolean(status)
      ? PayWithCoinsCardSpendingState.insufficient_coins
      : decideCardState(productData);

  const cardTitle = t({ id: decideCardTitle(type) });
  const modalTitle = t({ id: 'card_coin_spending.insufficient_coins.modal_title' }).replace(/{item}/gi, name);
  const transactionId = productData.coinsTransactionForProductId.transaction?.id;

  const handleOnSpend = async () => {
    if (userBalance < bundleValue) {
      setIsModalOpen(true);

      return;
    }

    try {
      if (!spendCoinsOnProduct.isLoading) {
        await spendCoinsOnProduct.mutateAsync({ productId: `${id}` });
        //Reloading window to give WP a chance to refetch product data
        window.location.reload();
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.log(err);
      }
    }
  };

  const handleOnCancel = async () => {
    try {
      if (transactionId && !updateCoinTransactionStatus.isLoading) {
        await updateCoinTransactionStatus.mutateAsync({ id: transactionId, status: 'cancelled' });
        window.location.reload();
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.log(err);
      }
    }
  };

  return (
    <LoadingOverlay isLoading={settingsDataLoading || balanceDataLoading || productDataLoading}>
      <S.Wrapper>
        <PayWithCoinsCard
          state={state}
          userBalance={userBalance}
          bundleValue={bundleValue}
          canCancel={canCancel}
          onSpend={handleOnSpend}
          onCancel={handleOnCancel}
          title={{
            readyToSpend: cardTitle,
            insufficientCoins: cardTitle,
          }}
        />
        {state === PayWithCoinsCardSpendingState.insufficient_coins && (
          <Modal
            isOpened={isModalOpen}
            hasCloseButton={true}
            title={modalTitle}
            onClose={() => {
              setIsModalOpen(false);
            }}
          >
            <S.ModalWrapper>
              <S.ModalText>{t({ id: 'card_coin_spending.insufficient_coins.modal_text' })}</S.ModalText>
              <S.ModalBalanceWrapper>{decideCoinsBalanceByType(type, bundleValue, userBalance)}</S.ModalBalanceWrapper>
              <S.ModalButtonWrapper>
                <Button href="/coins">{t({ id: 'card_coin_spending.insufficient_coins.modal_button' })}</Button>
              </S.ModalButtonWrapper>
            </S.ModalWrapper>
          </Modal>
        )}
      </S.Wrapper>
    </LoadingOverlay>
  );
};
